import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Search, Inbox } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic data module to power simple CRUD screens.
 * fields: [{ key, label, type, required, options, render, default }]
 * columns: [{ key, label, render }]
 */
export default function DataModule({
  title,
  subtitle,
  endpoint,
  fields,
  columns,
  pageTestId,
  emptyText = "No records yet. Click 'Add' to create one.",
  initialRecord,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialRecord || {});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${endpoint}`);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [endpoint]);

  const openCreate = () => {
    setEditing(null);
    const init = {};
    fields.forEach((f) => { if (f.default !== undefined) init[f.key] = f.default; });
    setForm({ ...(initialRecord || {}), ...init });
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/${endpoint}/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post(`/${endpoint}`, form);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name || item.id}?`)) return;
    try {
      await api.delete(`/${endpoint}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = search
    ? items.filter((it) => JSON.stringify(it).toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="space-y-6" data-testid={pageTestId}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="text-xs sm:text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="flex gap-2 items-center w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full sm:w-56"
              data-testid={`${pageTestId}-search`}
            />
          </div>
          <Button onClick={openCreate} data-testid={`${pageTestId}-add-btn`}>
            <Plus size={16} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Inbox size={36} className="mx-auto text-muted-foreground" />
            <div className="mt-4 text-sm text-muted-foreground">{emptyText}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[600px]">
              <TableHeader>
                <TableRow>
                  {columns.map((c) => (
                    <TableHead key={c.key}>{c.label}</TableHead>
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((it) => (
                  <TableRow key={it.id} data-testid={`${pageTestId}-row-${it.id}`}>
                    {columns.map((c) => (
                      <TableCell key={c.key}>
                        {c.render ? c.render(it) : (it[c.key] ?? "—")}
                      </TableCell>
                    ))}
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(it)} data-testid={`delete-${it.id}`} className="text-destructive">
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mt-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs font-medium">{f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}</Label>
                {f.type === "textarea" ? (
                  <textarea
                    className="w-full mt-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={3}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`field-${f.key}`}
                  />
                ) : f.type === "select" ? (
                  <select
                    className="w-full mt-1 rounded-lg border border-input bg-background px-3 py-2 text-sm h-10"
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`field-${f.key}`}
                  >
                    <option value="">Select…</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input
                    type={f.type || "text"}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? parseFloat(e.target.value || 0) : e.target.value })}
                    data-testid={`field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-btn">{editing ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
